// src/components/assistant/ErrorBoundary.tsx
import React, { Component, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Fallback component to render on error. If not provided, uses default fallback. */
  fallback?: ReactNode | ((error: Error, reset: () => void) => ReactNode);
  /** Optional callback when an error occurs */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /** Variant controls the visual style */
  variant?: "inline" | "card" | "minimal";
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary for gracefully handling errors in assistant components.
 * Use this to wrap charts, markdown rendering, and other potentially error-prone components.
 */
export class AssistantErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.props.onError?.(error, errorInfo);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      const { fallback, variant = "inline" } = this.props;

      if (fallback) {
        if (typeof fallback === "function") {
          return fallback(this.state.error!, this.reset);
        }
        return fallback;
      }

      return <DefaultErrorFallback variant={variant} error={this.state.error} onReset={this.reset} />;
    }

    return this.props.children;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Default Fallback Components
// ─────────────────────────────────────────────────────────────────────────────

interface DefaultErrorFallbackProps {
  variant: "inline" | "card" | "minimal";
  error: Error | null;
  onReset: () => void;
}

const DefaultErrorFallback: React.FC<DefaultErrorFallbackProps> = ({ variant, error, onReset }) => {
  if (variant === "minimal") {
    return (
      <span className="text-rose-400 text-sm">
        Error loading content
      </span>
    );
  }

  if (variant === "card") {
    return (
      <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-rose-500/20 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-rose-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-rose-300">Failed to render component</h4>
            <p className="text-xs text-rose-400/70 mt-0.5 truncate">
              {error?.message || "An unexpected error occurred"}
            </p>
          </div>
          <button
            type="button"
            onClick={onReset}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-500/20 text-rose-300 text-xs font-medium hover:bg-rose-500/30 transition-colors"
          >
            <RefreshCw className="h-3 w-3" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Default: inline variant
  return (
    <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/20">
      <AlertTriangle className="h-4 w-4 text-rose-400 flex-shrink-0" />
      <span className="text-sm text-rose-300">
        {error?.message || "Failed to render content"}
      </span>
      <button
        type="button"
        onClick={onReset}
        className="p-1 rounded hover:bg-rose-500/20 transition-colors"
        title="Retry"
      >
        <RefreshCw className="h-3.5 w-3.5 text-rose-400" />
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Specialized Error Boundaries
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Error boundary specifically for chart rendering.
 * Shows a placeholder that maintains visual consistency.
 */
export const ChartErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => (
  <AssistantErrorBoundary
    variant="card"
    fallback={(error, reset) => (
      <div className="h-[320px] rounded-xl border border-gray-700/40 bg-gray-900/40 flex flex-col items-center justify-center p-6">
        <div className="h-12 w-12 rounded-xl bg-rose-500/10 flex items-center justify-center mb-4">
          <AlertTriangle className="h-6 w-6 text-rose-400" />
        </div>
        <h4 className="text-sm font-medium text-gray-300 mb-1">Chart rendering failed</h4>
        <p className="text-xs text-gray-500 text-center mb-4 max-w-[200px]">
          {error.message || "An error occurred while rendering this chart"}
        </p>
        <button
          type="button"
          onClick={reset}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-800 text-gray-300 text-sm hover:bg-gray-700 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Try Again
        </button>
      </div>
    )}
  >
    {children}
  </AssistantErrorBoundary>
);

/**
 * Error boundary for markdown/rich content parsing.
 * Falls back to showing the raw text.
 */
export const ContentErrorBoundary: React.FC<{ 
  children: ReactNode; 
  rawContent?: string;
}> = ({ children, rawContent }) => (
  <AssistantErrorBoundary
    variant="inline"
    fallback={
      rawContent ? (
        <div className="space-y-2">
          <div className="text-xs text-rose-400 flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" />
            Content parsing failed - showing raw text
          </div>
          <pre className="text-sm text-gray-300 whitespace-pre-wrap font-mono">
            {rawContent}
          </pre>
        </div>
      ) : undefined
    }
  >
    {children}
  </AssistantErrorBoundary>
);

export default AssistantErrorBoundary;

